import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SgTextVariant } from '../sg-text/sg-text.component';

export type SgLinkSize = 'default' | 'small';
export type SgLinkVariant = 'brand' | 'neutral' | 'button-primary';

const EXTERNAL_LINK_PREFIXES = ['mailto:', 'tel:', 'http://', 'https://'];

/**
 * A styled link component that supports internal navigation, external URLs, and click-only actions.
 *
 * @example
 * // Internal route (uses Angular routerLink)
 * <sg-link link="/dashboard" text="Go to Dashboard"></sg-link>
 *
 * // External URL (uses native href)
 * <sg-link link="https://example.com" text="Visit Site" target="_blank"></sg-link>
 *
 * // Protocol links (mailto, tel)
 * <sg-link link="mailto:support@example.com" text="Contact Support"></sg-link>
 *
 * // Click-only action (no navigation)
 * <sg-link text="Perform Action" (sgClick)="onAction()"></sg-link>
 */
@Component({
  selector: 'sg-link',
  templateUrl: './sg-link.component.html',
  styleUrls: ['./sg-link.component.scss'],
  standalone: false,
})
export class SgLinkComponent {
  /** Size of the link text. Omit to inherit from parent. */
  @Input() size?: SgLinkSize;

  /** Color variant of the link. */
  @Input() variant: SgLinkVariant = 'brand';

  /**
   * URL or route the link points to.
   * - External URLs (http://, https://, mailto:, tel:) use native href
   * - Internal paths use Angular routerLink
   * - Omit to create a click-only action link
   */
  @Input() link: string;

  /** Target attribute for the link. */
  @Input() target: '_blank' | '_self' | '_parent' | '_top' = '_self';

  /** Text content of the link. */
  @Input() text = '';

  /** Whether to show the trailing icon. */
  @Input() showIcon = false;

  /** Material icon name to display. */
  @Input() icon = 'chevron_right';

  /** Whether the link is disabled. */
  @Input() disabled = false;

  /** Additional CSS class(es) to apply. */
  @Input() cssClass = '';

  /** State to pass to the router when navigating (internal links only). */
  @Input() state: { [k: string]: any };

  /** Emitted when the link is clicked (only fires when not disabled). */
  @Output() sgClick = new EventEmitter<MouseEvent>();

  get hasLink(): boolean {
    return !!this.link;
  }

  get isExternalLink(): boolean {
    if (!this.link) return false;

    return EXTERNAL_LINK_PREFIXES.some((prefix) => this.link.startsWith(prefix));
  }

  get linkClasses(): string[] {
    const classes = ['sg-link', `sg-link--${this.variant}`];

    if (this.size) {
      classes.push(`sg-link--${this.size}`);
    } else {
      classes.push('sg-link--inherit');
    }

    if (this.disabled) {
      classes.push('sg-link--disabled');
    }

    if (this.cssClass) {
      classes.push(this.cssClass);
    }

    return classes;
  }

  get textVariant(): SgTextVariant | null {
    if (!this.size) {
      return null;
    }
    return this.size === 'small' ? 'sg-label-small-regular' : 'sg-label-medium-regular';
  }

  get isSizeProvided(): boolean {
    return this.size !== undefined;
  }

  onClick(event: MouseEvent): void {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();

      return;
    }

    this.sgClick.emit(event);
  }
}
